import React, { useEffect, useState } from "react";
import { RiDeleteBin5Line } from "react-icons/ri";
import { CiSearch } from "react-icons/ci";
import { useDispatch, useSelector } from "react-redux";
import { deleteUserPaymentHistory, userPaymentHistory } from "../../Redux/Actions/PaymentAction";
import { CircularProgress } from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import AnimatedCounter from "../../utils/AnimatedCounter";

const PaymentHistory = () => {
  const [paymentId, setPaymentId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const dispatch = useDispatch();
  const { paymentData, loading } = useSelector((state) => state.Payment) || {};
  const paymentRecords = paymentData?.payments || [];

  // Status display logic
  const getDisplayStatus = (status) => {
    if (status === "succeeded") return "Success";
    if (status === "Pending") return "Pending";
    if (status === "Failed") return "Failed";
    return status;
  };

  // Styling functions for status
  const getStatusClass = (status) => {
    switch (status) {
      case "succeeded":
        return "bg-[#11A0111A] text-[#11A011]";
      case "Pending":
        return "bg-[#CE910C1A] text-[#CE910C]";
      case "Failed":
        return "bg-[#E728481A] text-[#E72848]";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };
  const getCircleColor = (status) => {
    switch (status) {
      case "succeeded":
        return "bg-[#11A011]"; // Green circle
      case "Pending":
        return "bg-[#CE910C]"; // Orange circle
      case "Failed":
        return "bg-[#E72848]"; // Red circle
      default:
        return "bg-gray-400"; // Default gray circle
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch payment history on mount and when debouncedSearch changes
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        await dispatch(userPaymentHistory(debouncedSearch));
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    };
    fetchHistory();
  }, [dispatch, debouncedSearch]);

  const handleOpenDeleteModal = (id) => {
    setPaymentId(id);
    setConfirmDelete(true);
  };

  const handleDeleteHistory = async () => {
    try {
      await dispatch(deleteUserPaymentHistory(paymentId));
      toast.success("History Deleted");
      setPaymentId(null);
      setConfirmDelete(false);
      await dispatch(userPaymentHistory());
    } catch (error) {
      toast.error(error?.response?.data?.message || "Please try Again..!!");
    }
  };

  return (
    <div className="w-full mx-auto py-6 bg-white">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
        <div className="text-lg md:text-xl font-semibold mb-2 md:mb-0">
          Payment History (<AnimatedCounter total={paymentRecords.length}/>)
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center text-gray-500">
            <CiSearch size={20} />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#E72848]"
          />
        </div>
      </div>
      <div className="overflow-x-auto border border-[#E8E8E8] rounded-lg">
        <table className="w-full border-collapse ">  
          <thead>
            <tr className="bg-[#F7F7F7] text-[#888888] text-left">
              <th className="p-3">Name</th>
              <th className="p-3">Date</th>
              <th className="p-3">Status</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-3">
                  <CircularProgress size={24} color="inherit" />
                </td>
              </tr>
            ) : paymentRecords.length > 0 ? (
              paymentRecords.map((payment, index) => (
                <tr key={index} className="border-t border-[#E8E8E8]">
                  <td className="p-3">{payment.profile_user.firstname}</td>
                  <td className="p-3 whitespace-nowrap">
                    {new Date(payment.updated_at).toLocaleString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}
                  </td>
                  <td className="p-3 flex my-8 sm:my-0">
                    <div className={`px-3 py-1 rounded-full flex gap-2 items-center ${getStatusClass(payment.status)}`}>
                      <span className={`h-2 w-2 rounded-full ${getCircleColor(payment.status)}`}></span>
                      <span >{getDisplayStatus(payment.status)}</span>
                    </div>
                  </td>
                  <td className="p-3">{payment.amount}</td>
                  <td className="p-3 text-red-500 cursor-pointer hover:text-red-700">
                    <RiDeleteBin5Line size={20} onClick={() => handleOpenDeleteModal(payment.id)} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-20">
                  No Payment History Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 p-4 sm:p-0" onClick={() => setConfirmDelete(false)}>
          <div className="bg-white p-6 rounded-3xl shadow-lg max-w-xs" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end">
              <IoMdClose size={20} className=" cursor-pointer" onClick={() => setConfirmDelete(false)} />
            </div>
            <div className="flex justify-center mt-3">
              <div className="border-2 border-[#E72848] rounded-full p-4">
                <RiDeleteBin5Line size={30} color="#E72848" />
              </div>
            </div>
            <div className="px-4 py-8 flex flex-col justify-center items-center text-center">
              <div>Are you sure you want to delete this payment history?</div>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-5">
              <button
                className="px-6 py-2 border border-gray-300 rounded-xl w-full sm:w-auto"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
              <button onClick={handleDeleteHistory} className="px-6 py-2 bg-[#E72848] text-white rounded-xl w-full sm:w-auto">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
